import { useState, useMemo, useEffect, useRef } from 'react';

import Alert from '../../../alert';

import { stripParentheses } from '../../../../../utils';

import { decisionTypes } from './data';
import {
  beDecisionTypeCards,
  beRecommendedByLevel,
  beModalCopy,
} from './beModalData';

import './balancedEquationLevelModal.css';

const STEP_DECISION = 'decision';
const STEP_SCENARIO = 'scenario';

// Stable empty-array reference used as the default for the
// editingScenarioLevelPreCheckedItems prop. Inlining `= []` would
// produce a new array reference on every render, which would re-fire
// the auto-check effect (whose deps include this prop) and clobber
// the instructor's manual unchecks immediately.
const EMPTY_PRECHECKED = [];

// New 2-step add-level flow for Balanced Equation. Mirrors the
// existing modal's onSave callback signature so the parent's
// handleBalancedPrinciplesAccontSelectSaveButtonClick wiring is
// unchanged.
//
// Props:
//   scenarioLevelNumber – for the title and recommended-card badge
//   filterItems         – { [decisionTypeLabel]: { [scenarioLevelName]: transactions[] } }
//   isButtonLoading     – Save button spinner state
//   onCloseButtonClick  – modal close
//   onSaveButtonClick   – (selectedTransactions, scenarioLevelName,
//                         decisionTypeLabel, savedCallback,
//                         gameType, decisionTypeKey) => void
//   decisionType                          – (edit mode) decision type
//                         key already on the level being edited; if
//                         truthy, the modal opens directly on step 2
//                         and hides the Change Decision Type button.
//   editingScenarioLevelPreCheckedItems – (edit mode) the level's
//                         currently saved questions, used to pre-check
//                         and to infer which source scenario to open.
export default function BalancedEquationLevelModal({
  scenarioLevelNumber,
  filterItems,
  isButtonLoading,
  onCloseButtonClick,
  onSaveButtonClick,
  decisionType = '',
  editingScenarioLevelPreCheckedItems = EMPTY_PRECHECKED,
}) {
  const gameType = 'Balanced Equation';
  const labelByKey = decisionTypes[gameType] || {};
  const isEditMode = !!decisionType;

  const cards = useMemo(
    () => beDecisionTypeCards.filter((c) => c.key !== 'all-types'),
    []
  );

  const [step, setStep] = useState(isEditMode ? STEP_SCENARIO : STEP_DECISION);
  const [leavingStep, setLeavingStep] = useState(null);
  const [selectedDecisionTypeKey, setSelectedDecisionTypeKey] = useState(
    isEditMode ? decisionType : null
  );
  const [selectedScenarioName, setSelectedScenarioName] = useState(null);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [pendingScenarioName, setPendingScenarioName] = useState(null);
  // Equation picker: shown after Save for a NEW Increase-or-Decrease
  // level so the instructor can pick which accounting-equation form
  // the game should render. Every other decision type auto-saves with
  // equation='assets' (which forces the additive `A = L + E` form —
  // required whenever a Debit/Credit chip is on the board, and the
  // safe default for the Accounts Impacted / ISI cards). Edit mode
  // never shows this picker; a separate future toggle inside the
  // modal will let instructors flip the equation on an existing level.
  const [isEquationPickerVisible, setIsEquationPickerVisible] = useState(false);

  // True until the first effect run that loads edit-mode pre-checked
  // items. After that, scenario changes pre-check ALL transactions of
  // the new scenario (matching create-mode default).
  const editInitPendingRef = useRef(isEditMode);

  const recommendedKey = beRecommendedByLevel[scenarioLevelNumber] || null;

  const selectedCard = useMemo(
    () => beDecisionTypeCards.find((c) => c.key === selectedDecisionTypeKey) || null,
    [selectedDecisionTypeKey]
  );

  // The decision-type label is the key into filterItems (e.g.
  // 'Increase or Decrease'), and within that the scenario-level
  // names (e.g. 'food-truck') key into the transactions array.
  const decisionTypeLabel = selectedDecisionTypeKey ? labelByKey[selectedDecisionTypeKey] : null;
  const scenarioBucket = decisionTypeLabel ? filterItems?.[decisionTypeLabel] : null;
  const scenarioNames = useMemo(
    () => (scenarioBucket ? Object.keys(scenarioBucket) : []),
    [scenarioBucket]
  );

  // In edit mode, find the source scenario whose items overlap the
  // most with the level's existing questions (matched by text). Falls
  // back to the first scenario if no overlap is found. This is just
  // the default opening scenario — the instructor can navigate to a
  // different one (subject to the cross-scenario warning).
  const initialEditScenarioName = useMemo(() => {
    if (!isEditMode || !scenarioBucket || !editingScenarioLevelPreCheckedItems.length) {
      return null;
    }
    const precheckedTexts = new Set(
      editingScenarioLevelPreCheckedItems.map((p) => p.text)
    );
    let best = null;
    let bestScore = 0;
    Object.entries(scenarioBucket).forEach(([name, items]) => {
      const score = items.filter((it) => precheckedTexts.has(it.text)).length;
      if (score > bestScore) {
        best = name;
        bestScore = score;
      }
    });
    return best;
  }, [isEditMode, scenarioBucket, editingScenarioLevelPreCheckedItems]);

  // Default-select a scenario when entering step 2 (edit mode prefers
  // the inferred source, create mode picks the first). Clear when
  // going back to step 1.
  useEffect(() => {
    if (step === STEP_SCENARIO && !selectedScenarioName && scenarioNames.length) {
      setSelectedScenarioName(initialEditScenarioName || scenarioNames[0]);
    }
    if (step === STEP_DECISION) {
      setSelectedScenarioName(null);
      setSelectedTransactions([]);
    }
  }, [step, scenarioNames, selectedScenarioName, initialEditScenarioName]);

  const visibleTransactions = useMemo(() => {
    if (!scenarioBucket || !selectedScenarioName) return [];
    return scenarioBucket[selectedScenarioName] || [];
  }, [scenarioBucket, selectedScenarioName]);

  // Pre-check transactions when the active scenario changes. On the
  // very first effect run in edit mode, pre-check only items that
  // match the level's saved questions by text (so the modal opens
  // showing the instructor's existing picks). On subsequent scenario
  // changes — and for create mode entirely — pre-check every visible
  // transaction so Save is enabled by default and the cross-scenario
  // warning correctly fires once the user customizes.
  //
  // visibleTransactions is intentionally NOT a dep — including it
  // would re-fire the effect on every parent re-render that produces
  // a fresh useMemo result for filterItems, which would clobber the
  // user's manual unchecks. We read the latest value from closure.
  useEffect(() => {
    if (step !== STEP_SCENARIO || !selectedScenarioName) return;

    if (editInitPendingRef.current && editingScenarioLevelPreCheckedItems.length) {
      const precheckedTexts = new Set(
        editingScenarioLevelPreCheckedItems.map((p) => p.text)
      );
      setSelectedTransactions(
        visibleTransactions.filter((t) => precheckedTexts.has(t.text))
      );
      editInitPendingRef.current = false;
      return;
    }

    setSelectedTransactions(visibleTransactions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedScenarioName, editingScenarioLevelPreCheckedItems]);

  const handleCardClick = (key) => {
    if (step !== STEP_DECISION) return;
    setSelectedDecisionTypeKey(key);
    setStep(STEP_SCENARIO);
  };

  const handleChangeDecisionType = () => {
    setLeavingStep(STEP_SCENARIO);
    setTimeout(() => {
      setStep(STEP_DECISION);
      setSelectedDecisionTypeKey(null);
      setSelectedScenarioName(null);
      setSelectedTransactions([]);
      setLeavingStep(null);
    }, 220);
  };

  const handleScenarioRadioClick = (name) => {
    if (name === selectedScenarioName) return;

    // Warning fires only when the current selection has diverged from
    // the auto-checked default (every transaction of the current
    // scenario). If the instructor hasn't customized — i.e. all
    // current-scenario transactions are still checked — the switch is
    // silent because nothing the user picked is actually being lost.
    const hasCustomized =
      selectedTransactions.length !== visibleTransactions.length ||
      !visibleTransactions.every((t) =>
        selectedTransactions.some((s) => s.id === t.id)
      );

    if (hasCustomized && selectedTransactions.length > 0) {
      setPendingScenarioName(name);
      return;
    }
    setSelectedScenarioName(name);
  };

  const handleScenarioSwitchConfirm = () => {
    setSelectedScenarioName(pendingScenarioName);
    setSelectedTransactions([]);
    setPendingScenarioName(null);
  };

  const handleScenarioSwitchCancel = () => {
    setPendingScenarioName(null);
  };

  const handleTransactionToggle = (item, isChecked) => {
    setSelectedTransactions((prev) => {
      if (isChecked) {
        if (prev.some((t) => t.id === item.id)) return prev;
        return [...prev, item];
      }
      return prev.filter((t) => t.id !== item.id);
    });
  };

  // "Select all / Deselect all" toggle above the transactions column.
  // Operates only on the currently-visible list (i.e. the selected
  // scenario). Individual checkboxes stay independently controllable
  // afterward; Save only submits whatever is checked at that moment.
  const areAllVisibleSelected =
    visibleTransactions.length > 0
    && visibleTransactions.every((t) => selectedTransactions.some((s) => s.id === t.id));
  const areNoneVisibleSelected =
    visibleTransactions.length > 0
    && !visibleTransactions.some((t) => selectedTransactions.some((s) => s.id === t.id));

  // Sticky label: the toggle only flips when the user clicks it OR
  // when the visible selection hits a boundary (all → 'deselect',
  // none → 'select'). A single incidental uncheck no longer swaps
  // the label out from under the user.
  const [selectAllMode, setSelectAllMode] = useState('select');
  useEffect(() => {
    if (areAllVisibleSelected) setSelectAllMode('deselect');
    else if (areNoneVisibleSelected) setSelectAllMode('select');
  }, [areAllVisibleSelected, areNoneVisibleSelected]);

  const handleToggleAllTransactions = () => {
    if (selectAllMode === 'deselect') {
      const visibleIds = new Set(visibleTransactions.map((t) => t.id));
      setSelectedTransactions((prev) => prev.filter((t) => !visibleIds.has(t.id)));
    } else {
      setSelectedTransactions((prev) => {
        const merged = [...prev];
        visibleTransactions.forEach((t) => {
          if (!merged.some((s) => s.id === t.id)) merged.push(t);
        });
        return merged;
      });
    }
  };

  // Wrap the parent save so we can inject the picked/inferred equation
  // as the seventh arg. Edit mode passes `undefined` — the parent then
  // omits the field from the API payload and the backend keeps whatever
  // value the level already carries.
  const performSave = (equation) => {
    onSaveButtonClick(
      selectedTransactions,
      selectedScenarioName,
      decisionTypeLabel,
      onCloseButtonClick,
      gameType,
      selectedDecisionTypeKey,
      equation
    );
  };

  const handleSaveClick = () => {
    if (!selectedTransactions.length) return;
    // Edit mode: never touch equation — a future in-modal toggle will
    // handle changes on existing levels.
    if (isEditMode) {
      performSave(undefined);
      return;
    }
    // Only Increase-or-Decrease surfaces the picker. Every other card
    // saves with equation='assets' immediately.
    if (selectedDecisionTypeKey === 'increase-decrease') {
      setIsEquationPickerVisible(true);
      return;
    }
    performSave('assets');
  };

  const handleEquationPick = (equation) => {
    setIsEquationPickerVisible(false);
    performSave(equation);
  };

  const renderCard = (card, opts = {}) => {
    const { pinned = false, allowClick = true, footer = null, disabled = false } = opts;
    const isRecommended = card.key === recommendedKey;
    const isSelected = card.key === selectedDecisionTypeKey;
    const isClickable = allowClick && !pinned && !disabled;
    return (
      <div
        key={card.key}
        className={[
          'be-level-card',
          pinned ? 'be-level-modal-pinned-card' : '',
          disabled ? 'be-level-card--disabled' : '',
        ].filter(Boolean).join(' ')}
        data-selected={isSelected ? 'true' : 'false'}
        data-disabled={disabled ? 'true' : 'false'}
        onClick={isClickable ? () => handleCardClick(card.key) : undefined}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        aria-disabled={disabled ? 'true' : undefined}
      >
        <h3 className="be-level-card-title">{card.title}</h3>
        <hr className="be-level-card-divider" />
        <p className="be-level-card-description">
          {card.description.map((seg, i) =>
            seg.bold ? <b key={i}>{seg.text}</b> : <span key={i}>{seg.text}</span>
          )}
        </p>
        {isRecommended && !pinned && (
          <div className="be-level-card-recommended">
            {beModalCopy.recommendedBadge.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        )}
        {footer}
      </div>
    );
  };

  return (
    <div className="be-level-modal-overlay">
      <div
        className="be-level-modal"
        data-step={step}
        data-leaving={leavingStep || undefined}
      >
        <button
          className="be-level-modal-close"
          onClick={onCloseButtonClick}
          aria-label="Close"
        >
          ×
        </button>

        <div className="be-level-modal-header">
          <h2 className="be-level-modal-title">
            {step === STEP_DECISION
              ? beModalCopy.title(scenarioLevelNumber)
              : beModalCopy.step2Title(scenarioLevelNumber)}
          </h2>
          <p className="be-level-modal-subtitle">
            {step === STEP_DECISION
              ? beModalCopy.subtitle
              : beModalCopy.step2Subtitle}
          </p>
        </div>

        {step === STEP_DECISION && (
          <div className="be-level-modal-cards">
            {cards.map((card) => renderCard(card, {
              // Level 1 of a BE assignment is always Increase or
              // Decrease — the other decision types build on it, so
              // they're disabled here. Every later level keeps all
              // four cards clickable.
              disabled: scenarioLevelNumber === 1 && card.key !== 'increase-decrease',
            }))}
          </div>
        )}

        {step === STEP_SCENARIO && selectedCard && (
          <div className="be-level-modal-step2">
            {renderCard(selectedCard, {
              pinned: true,
              allowClick: false,
              footer: isEditMode ? null : (
                <button
                  type="button"
                  className="be-level-modal-back-button be-level-modal-pinned-back-button"
                  onClick={handleChangeDecisionType}
                >
                  {beModalCopy.changeDecisionTypeButton}
                </button>
              ),
            })}

            <div className="be-level-modal-step2-columns">
              <div>
                <h3 className="be-level-modal-column-header">
                  {beModalCopy.scenariosColumnHeader}
                </h3>
                <div className="be-level-modal-scenarios">
                  {scenarioNames.map((name) => {
                    const active = name === selectedScenarioName;
                    return (
                      <label
                        key={name}
                        className={`be-level-modal-scenario-option${active ? ' be-level-modal-scenario-option--active' : ''}`}
                      >
                        <input
                          type="radio"
                          name="be-business-scenario"
                          checked={active}
                          onChange={() => handleScenarioRadioClick(name)}
                        />
                        <span>{stripParentheses(name)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="be-level-modal-column-header-row">
                  <h3 className="be-level-modal-column-header">
                    {beModalCopy.transactionsColumnHeader}
                  </h3>
                  {visibleTransactions.length > 0 && (
                    <button
                      type="button"
                      className="be-level-modal-toggle-all"
                      onClick={handleToggleAllTransactions}
                    >
                      {selectAllMode === 'deselect' ? 'Deselect all' : 'Select all'}
                    </button>
                  )}
                </div>
                <div className="be-level-modal-transactions">
                  {visibleTransactions.map((item) => {
                    const checked = selectedTransactions.some((t) => t.id === item.id);
                    return (
                      <label key={item.id} className="be-level-modal-transaction-row">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => handleTransactionToggle(item, e.target.checked)}
                        />
                        <span>{item.text}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="be-level-modal-footer">
          <span />
          {step === STEP_SCENARIO && (
            <button
              type="button"
              className={`button-blue${isButtonLoading ? ' loading' : ''}`}
              disabled={!selectedTransactions.length || isButtonLoading}
              onClick={handleSaveClick}
            >
              {beModalCopy.saveButton}
            </button>
          )}
        </div>
      </div>

      {pendingScenarioName && (
        <Alert
          type="warning"
          title={beModalCopy.switchScenarioWarningTitle}
          text={beModalCopy.switchScenarioWarningText}
          confirmButtonText="Yes"
          cancelButtonText="No"
          onConfirmButtonClick={handleScenarioSwitchConfirm}
          onCancelButtonClick={handleScenarioSwitchCancel}
        />
      )}

      {isEquationPickerVisible && (
        <div className="be-equation-picker-overlay">
          <div className="be-equation-picker" role="dialog" aria-modal="true">
            <h2 className="be-equation-picker-title">Choose the Accounting Equation Formula</h2>
            <p className="be-equation-picker-body">
              This level is set to use the <b>Equity equation</b> (Assets −
              Liabilities = Equity) so students can build intuition by seeing
              the accounting equation in a different light, reasoning about
              what's left after settling liabilities instead of jumping
              straight to the standard textbook form. If you'd prefer the
              classical Assets equation, you can switch below.
            </p>
            <p className="be-equation-picker-body">
              Select which formula to display in this level.
            </p>
            <div className="be-equation-picker-buttons">
              <button
                type="button"
                className="be-equation-picker-button be-equation-picker-button--equity"
                disabled={isButtonLoading}
                onClick={() => handleEquationPick('equity')}
              >
                Assets − Liabilities = Equity
              </button>
              <button
                type="button"
                className="be-equation-picker-button be-equation-picker-button--assets"
                disabled={isButtonLoading}
                onClick={() => handleEquationPick('assets')}
              >
                Assets = Liabilities + Equity
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
